import React from "react";
import {
  X,
  Calendar,
  DollarSign,
  MapPin,
  Building2,
  FileText,
  Pill,
  Activity,
  Download,
  ExternalLink,
} from "lucide-react";

const HealthRecordModal = ({ record, onClose }) => {
  const formatDate = (date) => {
    if (!date) return "Unknown";
    if (date.toDate) return date.toDate().toLocaleDateString();
    return new Date(date).toLocaleDateString();
  };

  const formatCurrency = (amount, currency) => {
    const symbol = currency?.symbol || "$";
    return `${symbol}${(amount || 0).toFixed(2)}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden border border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center">
              <FileText className="w-5 h-5 mr-2 text-cyan-400" />
              Health Record Details
            </h2>
            <p className="text-gray-400 text-sm mt-1">
              {record.clinicName || "Unknown Clinic"} •{" "}
              {formatDate(record.date)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Information */}
            <div className="lg:col-span-2 space-y-6">
              {/* Basic Info */}
              <div className="bg-gray-700/50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <Building2 className="w-5 h-5 mr-2 text-cyan-400" />
                  Visit Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-gray-400 text-sm">Clinic Name</label>
                    <p className="text-white font-medium">
                      {record.clinicName || "Not specified"}
                    </p>
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm">Pet Name</label>
                    <p className="text-white font-medium">
                      {record.petName || "Not specified"}
                    </p>
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm">Date</label>
                    <p className="text-white font-medium flex items-center">
                      <Calendar className="w-4 h-4 mr-1 text-gray-400" />
                      {formatDate(record.date)}
                    </p>
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm">
                      Total Amount
                    </label>
                    <p className="text-white font-medium flex items-center">
                      <DollarSign className="w-4 h-4 mr-1 text-green-400" />
                      {formatCurrency(record.totalAmount, record.currency)}
                    </p>
                  </div>
                  {record.location && (
                    <div className="sm:col-span-2">
                      <label className="text-gray-400 text-sm">Location</label>
                      <p className="text-white font-medium flex items-center">
                        <MapPin className="w-4 h-4 mr-1 text-red-400" />
                        {record.location}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Line Items */}
              {record.lineItems && record.lineItems.length > 0 && (
                <div className="bg-gray-700/50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <Activity className="w-5 h-5 mr-2 text-green-400" />
                    Services & Treatments ({record.lineItems.length} items)
                  </h3>
                  <div className="space-y-4">
                    {(() => {
                      // Group items by category
                      const groupedItems = record.lineItems.reduce(
                        (groups, item) => {
                          const category = item.category || "General";
                          if (!groups[category]) {
                            groups[category] = [];
                          }
                          groups[category].push(item);
                          return groups;
                        },
                        {}
                      );

                      // Define color mapping for categories
                      const colorMap = {
                        blue: { bg: "bg-blue-500/20", text: "text-blue-400" },
                        purple: {
                          bg: "bg-purple-500/20",
                          text: "text-purple-400",
                        },
                        green: {
                          bg: "bg-green-500/20",
                          text: "text-green-400",
                        },
                        orange: {
                          bg: "bg-orange-500/20",
                          text: "text-orange-400",
                        },
                        cyan: { bg: "bg-cyan-500/20", text: "text-cyan-400" },
                        gray: { bg: "bg-gray-500/20", text: "text-gray-400" },
                        red: { bg: "bg-red-500/20", text: "text-red-400" },
                        yellow: {
                          bg: "bg-yellow-500/20",
                          text: "text-yellow-400",
                        },
                      };

                      return Object.entries(groupedItems).map(
                        ([category, items]) => {
                          const firstItem = items[0];
                          const categoryColor =
                            colorMap[firstItem?.color] || colorMap.gray;
                          const categoryTotal = items.reduce(
                            (sum, item) =>
                              sum +
                              (item.price ||
                                item.totalPrice ||
                                item.unitPrice ||
                                0),
                            0
                          );

                          return (
                            <div key={category} className="space-y-2">
                              {/* Category Header */}
                              <div
                                className={`flex items-center justify-between p-3 ${categoryColor.bg} rounded-lg border border-gray-600`}
                              >
                                <div className="flex items-center space-x-3">
                                  <span
                                    className={`text-xl ${categoryColor.text}`}
                                  >
                                    {firstItem?.icon || "🏥"}
                                  </span>
                                  <div>
                                    <h4 className="text-white font-semibold">
                                      {category}
                                    </h4>
                                    <p className="text-gray-300 text-sm">
                                      {items.length} item
                                      {items.length !== 1 ? "s" : ""}
                                    </p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p
                                    className={`font-bold ${categoryColor.text}`}
                                  >
                                    {formatCurrency(
                                      categoryTotal,
                                      record.currency
                                    )}
                                  </p>
                                </div>
                              </div>

                              {/* Category Items */}
                              <div className="space-y-2 ml-4">
                                {items.map((item, itemIndex) => (
                                  <div
                                    key={itemIndex}
                                    className="flex items-center justify-between p-3 bg-gray-700/30 rounded-lg border-l-4"
                                    style={{
                                      borderLeftColor:
                                        categoryColor.text.replace("text-", ""),
                                    }}
                                  >
                                    <div className="flex-1">
                                      <div className="flex items-start space-x-3">
                                        <div className="flex-1">
                                          <div className="flex items-center space-x-2 mb-1">
                                            <p className="text-white font-medium">
                                              {item.description ||
                                                "Unknown service"}
                                            </p>
                                            {item.subcategory &&
                                              item.subcategory !== category && (
                                                <span
                                                  className={`px-2 py-1 text-xs rounded-full ${categoryColor.bg} ${categoryColor.text} font-medium`}
                                                >
                                                  {item.subcategory}
                                                </span>
                                              )}
                                          </div>
                                          {item.quantity && (
                                            <p className="text-gray-400 text-sm">
                                              Quantity: {item.quantity}
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <p className="text-white font-semibold">
                                        {formatCurrency(
                                          item.price ||
                                            item.totalPrice ||
                                            item.unitPrice,
                                          record.currency
                                        )}
                                      </p>
                                      {item.unitPrice &&
                                        item.quantity &&
                                        item.unitPrice !== item.price && (
                                          <p className="text-gray-400 text-sm">
                                            {formatCurrency(
                                              item.unitPrice,
                                              record.currency
                                            )}{" "}
                                            each
                                          </p>
                                        )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        }
                      );
                    })()}
                  </div>

                  {/* Total */}
                  <div className="mt-4 pt-4 border-t border-gray-600">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold text-white">
                        Total
                      </span>
                      <span className="text-xl font-bold text-green-400">
                        {formatCurrency(record.totalAmount, record.currency)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Additional Notes */}
              {record.notes && (
                <div className="bg-gray-700/50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-white mb-3">
                    Notes
                  </h3>
                  <p className="text-gray-300">{record.notes}</p>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Original Invoice */}
              {record.imageUrl && (
                <div className="bg-gray-700/50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
                    <FileText className="w-5 h-5 mr-2 text-purple-400" />
                    Original Invoice
                  </h3>
                  <div className="space-y-3">
                    <div className="aspect-square bg-gray-600 rounded-lg overflow-hidden">
                      <img
                        src={record.imageUrl}
                        alt="Invoice"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex space-x-2">
                      <a
                        href={record.imageUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors text-sm"
                      >
                        <ExternalLink className="w-4 h-4" />
                        <span>View Full</span>
                      </a>
                      <a
                        href={record.imageUrl}
                        download={record.fileName || "invoice"}
                        className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors text-sm"
                      >
                        <Download className="w-4 h-4" />
                        <span>Download</span>
                      </a>
                    </div>
                  </div>
                </div>
              )}

              {/* Quick Stats */}
              <div className="bg-gray-700/50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-white mb-3">
                  Quick Stats
                </h3>
                <div className="space-y-3">
                  {/* Category breakdown */}
                  {record.lineItems &&
                    record.lineItems.length > 0 &&
                    (() => {
                      const categoryStats = record.lineItems.reduce(
                        (acc, item) => {
                          const category = item.category || "General";
                          if (!acc[category]) {
                            acc[category] = {
                              count: 0,
                              total: 0,
                              icon: item.icon || "🏥",
                            };
                          }
                          acc[category].count += 1;
                          acc[category].total +=
                            item.price ||
                            item.totalPrice ||
                            item.unitPrice ||
                            0;
                          return acc;
                        },
                        {}
                      );

                      return Object.entries(categoryStats).map(
                        ([category, stats]) => (
                          <div
                            key={category}
                            className="flex justify-between items-center"
                          >
                            <div className="flex items-center space-x-2">
                              <span className="text-sm">{stats.icon}</span>
                              <span className="text-gray-400 text-sm">
                                {category}
                              </span>
                            </div>
                            <div className="text-right">
                              <span className="text-white font-medium text-sm">
                                {stats.count}
                              </span>
                              <p className="text-gray-400 text-xs">
                                {formatCurrency(stats.total, record.currency)}
                              </p>
                            </div>
                          </div>
                        )
                      );
                    })()}

                  <div className="border-t border-gray-600 pt-3">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Upload Date</span>
                      <span className="text-white font-medium">
                        {formatDate(record.uploadDate)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Processing Info */}
              <div className="bg-gray-700/50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-white mb-3">
                  Processing Info
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    <span className="text-gray-300 text-sm">AI Processed</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                    <span className="text-gray-300 text-sm">
                      Data Extracted
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                    <span className="text-gray-300 text-sm">
                      Stored Securely
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-700">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default HealthRecordModal;
